/**
 * Unified Filter Toolbar — dropdown logic and default selection.
 *
 * Shape, Size, Metal and Sort all submit through onSubmit.
 *
 * Expects to run inside the xFiltersAndSortby Alpine scope
 * (onSubmit() is available on the scope).
 */
(function () {
  'use strict';

  window._mioFilterToolbarInit = function (container) {
    var cells = container.querySelectorAll('[data-ft-cell]');

    // ── Mobile: dropdowns become a bottom sheet ──
    // The bar scrolls horizontally on a phone, and a scroll container clips
    // its descendants — so a panel anchored to its cell gets cut off at the
    // bar's edge. Anchoring it outside the scroller fixed the clipping but
    // still left a narrow panel hanging off a half-scrolled cell, and using
    // position:fixed to escape is unreliable inside a touch-scrolling
    // container on iOS Safari.
    //
    // So on mobile the open panel is MOVED out of the bar into a sheet that
    // lives outside the scroller entirely — the same shape as the Filters
    // drawer. It is moved back into its cell on close, so desktop keeps the
    // plain anchored dropdown with no duplicated markup.
    var shell = container.closest('.mio-ft-shell') || container.parentElement;
    var sheet = shell ? shell.querySelector('[data-ft-sheet]') : null;
    // The toolbar's sticky wrapper carries backdrop-filter, which makes it the
    // containing block for position:fixed. Left in place the sheet's inset:0
    // resolved against that 46px-tall bar instead of the viewport, so the panel
    // rendered mid-page and partly above the fold. Reparented to <body>, which
    // has no such ancestor. The panel-into-sheet dance below is unaffected.
    if (sheet && sheet.parentElement !== document.body) document.body.appendChild(sheet);
    var sheetBody = sheet ? sheet.querySelector('[data-ft-sheet-body]') : null;
    var sheetTitle = sheet ? sheet.querySelector('[data-ft-sheet-title]') : null;
    var isMobile = function () { return window.matchMedia('(max-width: 767px)').matches; };
    var sheetCell = null;

    /* Escape, Tab containment and back-button dismissal. The panel declares
       aria-modal="true" and had none of the three: a keyboard shopper who
       opened Shape could only leave with a mouse, and Tab walked out into the
       page behind. Back used to leave the collection entirely, losing every
       filter that had been set. */
    var a11y = (window.MioSheet && sheet)
      ? window.MioSheet.attach(sheet, { onClose: function () { closeSheet(); } })
      : { opened: function () {}, closed: function () {} };

    var closeTimer = null;
    var pendingFinish = null;

    /* Switching straight from one cell to another calls closeSheet() then
       openSheet(). With the close deferred for its exit animation, the pending
       restore would fire 280ms later and put the NEW dropdown back into the OLD
       cell. So any pending close is flushed synchronously first. */
    function flushClose() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      if (pendingFinish) { var f = pendingFinish; pendingFinish = null; f(); }
    }

    function closeSheet() {
      if (!sheet || !sheetCell) return;
      var cell = sheetCell;
      sheetCell = null;
      document.documentElement.classList.remove('mio-ft-sheet-open');
      a11y.closed();

      /* Let it leave. The sheet used to be removed with `hidden` on frame 0 —
         it rose over 220ms and then simply ceased to exist, the only panel on
         the site that departed without moving. The dropdown is only returned to
         its cell once the exit has run, or it would vanish mid-animation. */
      flushClose();
      sheet.classList.add('is-closing');

      var finish = pendingFinish = function () {
        closeTimer = null;
        pendingFinish = null;
        sheet.classList.remove('is-closing');
        var dd = sheetBody.querySelector('.mio-ft-dropdown');
        if (dd) cell.appendChild(dd);           // put it back where CSS expects it
        cell.classList.remove('is-open');
        sheet.hidden = true;
      };

      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      closeTimer = setTimeout(finish, reduced ? 0 : 280);
    }

    function openSheet(cell) {
      if (!sheet) return false;
      var dd = cell.querySelector('.mio-ft-dropdown');
      if (!dd) return false;
      closeSheet();
      flushClose();              /* land the previous panel before adopting this one */
      sheet.classList.remove('is-closing');
      sheetCell = cell;
      if (sheetTitle) {
        var label = cell.querySelector('.mio-ft-label');
        sheetTitle.textContent = label ? label.textContent.trim() : '';
      }
      sheetBody.appendChild(dd);
      cell.classList.add('is-open');
      sheet.hidden = false;
      document.documentElement.classList.add('mio-ft-sheet-open');
      a11y.opened();
      return true;
    }

    function closeAll() {
      closeSheet();
      cells.forEach(function (c) { c.classList.remove('is-open'); });
    }

    if (sheet) {
      sheet.addEventListener('click', function (e) {
        if (e.target.closest('[data-ft-sheet-close]') || e.target.hasAttribute('data-ft-sheet-scrim')) {
          closeSheet();
        }
      });
    }
    // A phone rotated to landscape can cross the breakpoint mid-open.
    window.addEventListener('resize', function () { if (!isMobile()) closeSheet(); });

    // ── Dropdown toggle ──
    cells.forEach(function (cell) {
      if (!cell.querySelector('[data-ft-header]')) return;
      // Bound to the CELL, not the inner header row, so the cell's padding is
      // tappable as well — a tap landing just inside the visible box used to
      // do nothing.
      cell.addEventListener('click', function (e) {
        // On desktop the panel is a child of the cell, so option clicks would
        // bubble here and immediately re-toggle it.
        if (e.target.closest('.mio-ft-dropdown')) return;
        e.stopPropagation();
        var wasOpen = cell.classList.contains('is-open');
        closeAll();
        if (wasOpen) return;
        if (isMobile() && openSheet(cell)) return;
        cell.classList.add('is-open');
      });
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (!container.contains(e.target) && !(sheet && sheet.contains(e.target))) {
        closeAll();
      }
    });

    // ── Option click handlers ──
    container.querySelectorAll('[data-ft-option]').forEach(function (option) {
      // Captured now, not at click time: while the sheet is open the panel
      // lives outside its cell, so closest() would find nothing.
      var boundCell = option.closest('[data-ft-cell]');
      option.addEventListener('click', function () {
        var cell = boundCell;
        var dropdown = option.closest('.mio-ft-dropdown');
        var type = cell ? cell.dataset.ftCell : '';
        var valueEl = cell ? cell.querySelector('[data-ft-value]') : null;
        var options = dropdown ? dropdown.querySelectorAll('[data-ft-option]') : [];

        // Mark active
        options.forEach(function (o) { o.classList.remove('is-active'); });
        option.classList.add('is-active');

        // Update header value text
        if (valueEl) {
          valueEl.textContent = option.dataset.ftLabel || option.textContent.trim();
        }

        // Update header icon (shape) — outerHTML, because the option icon is an
        // <img>: its innerHTML is always "", which silently blanked the header
        // glyph on every pick. "All" has no glyph, so clear it rather than
        // leaving the previous shape's icon beside the word "All".
        if (type === 'shape') {
          var headerIcon = cell.querySelector('[data-ft-header-icon]');
          var optionIcon = option.querySelector('.mio-ft-option-shape-icon');
          if (headerIcon) {
            headerIcon.innerHTML = optionIcon ? optionIcon.outerHTML : '';
          }
        }

        // Update header dot (metal)
        if (type === 'metal') {
          var headerDot = cell.querySelector('[data-ft-header-dot]');
          var optionDot = option.querySelector('.mio-ft-option-metal-dot');
          if (headerDot && optionDot) {
            headerDot.style.setProperty(
              '--mio-ft-metal-color',
              optionDot.style.getPropertyValue('--mio-ft-metal-color')
            );
          }
        }

        // Close dropdown (and dismiss the sheet if this came from it)
        closeSheet();
        cell.classList.remove('is-open');

        // ── Functional filters ──

        if (type === 'metal') {
          // Uncheck ALL metal inputs page-wide (toolbar + facets drawer share FacetFiltersForm)
          var clickedInput = option.querySelector('.mio-ft-input');
          var paramName = clickedInput ? clickedInput.name : '';
          if (paramName) {
            document.querySelectorAll('input[name="' + paramName + '"]').forEach(function (inp) {
              inp.checked = false;
            });
          }
          container.querySelectorAll('.mio-ft-input[data-ft-type="metal"]').forEach(function (inp) {
            inp.checked = false;
          });
          if (clickedInput) clickedInput.checked = true;
          // Trigger filter
          triggerSubmit(container, 0);
        }

        if (type === 'sort') {
          var sortValue = option.dataset.ftSortValue;
          if (sortValue) {
            var select = document.getElementById('FormSelectFilter');
            if (select) {
              select.value = sortValue;
              triggerSubmit(container, 0);
            }
          }
        }

        if (type === 'shape' || type === 'size') {
          var isClear = option.hasAttribute('data-ft-clear');

          // onSubmit() serialises the whole #FacetFiltersForm, so a stale
          // checked input in the facets drawer would be submitted alongside
          // this one. Clear duplicates page-wide — but only those whose value
          // belongs to THIS dropdown, so clearing by name alone can never drop
          // a sibling dropdown that happens to share a param name.
          // During mobile dismissal the dropdown remains in the sheet for
          // 280ms, so its inputs are temporarily outside the toolbar.
          var ownInputs = Array.prototype.slice.call(
            (dropdown || container).querySelectorAll('.mio-ft-input[data-ft-type="' + type + '"]')
          );
          // The clear option carries no input of its own, so take the param
          // name from the cell rather than from the clicked element.
          var typedName = ownInputs.length ? ownInputs[0].name : '';
          var ownValues = {};
          ownInputs.forEach(function (inp) {
            ownValues[inp.value] = true;
            inp.checked = false;
          });
          if (typedName) {
            document
              .querySelectorAll('input[name="' + typedName + '"]')
              .forEach(function (inp) {
                if (ownValues[inp.value]) inp.checked = false;
              });
          }

          if (!isClear) {
            var typedInput = option.querySelector('.mio-ft-input');
            if (typedInput) typedInput.checked = true;
          }
          triggerSubmit(container, 0);
        }
      });
    });

    // ── Mirror the URL's filter state ──
    syncFromUrl(container);

    // The toolbar is never re-rendered by facets.js, so re-derive its state
    // whenever the grid is swapped (AJAX filter, chip removal) or history
    // moves. No custom event is dispatched, hence the observer.
    var gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer && window.MutationObserver) {
      var pending;
      new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(function () { syncFromUrl(container); }, 50);
      }).observe(gridContainer, { childList: true });
    }
    /* A sheet dismissing itself pops the history entry it pushed on open (see
       mio-sheet.js). That pop is NOT a navigation and must not re-read the URL:
       doing so re-ticked the checkboxes from the pre-selection URL, undoing the
       option the shopper had just tapped, ~350ms before the debounced submit
       serialised the form — so choosing Oval applied Round.

       Same marker check mio-collection-nav-ajax.js already uses. */
    window.addEventListener('popstate', function (e) {
      var key = (window.MioSheet && window.MioSheet.STATE_KEY) || 'mioSheet';
      if (e && e.state && e.state[key]) return;
      syncFromUrl(container);
    });

    // ── Stuck detection ──
    // The top divider should show at rest and hide only while the toolbar is
    // pinned under the sticky header (CSS has no :stuck selector). --top-header
    // is the live header height maintained by theme.js setPositionTop.
    var stickyWrap = container.closest('.mio-sticky-under-header');
    if (stickyWrap) {
      var stuckTick = false;
      var checkStuck = function () {
        stuckTick = false;
        var offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--top-header')) || 0;
        var stuck = stickyWrap.getBoundingClientRect().top <= offset + 1;
        stickyWrap.classList.toggle('mio-ft-stuck', stuck);
      };
      window.addEventListener('scroll', function () {
        if (!stuckTick) {
          stuckTick = true;
          requestAnimationFrame(checkStuck);
        }
      }, { passive: true });
      checkStuck();
    }
  };

  function triggerSubmit(container, resetPage) {
    /* The second argument lands on facets.js onSubmit(wait = 500) — despite the
       name it IS the debounce, and omitting it cost half a second of silence
       after every option tap: measured, the loading bar appeared ~600ms after
       the click and the grid settled at ~1,400ms, while the sheet had already
       closed. The debounce is there for typed price inputs; a discrete option
       tap has nothing to debounce. Sort always passed 0, which is why sort felt
       instant and the filters did not. */
    // Dispatch custom event — picked up by @mio-filter-submit.window on the toolbar
    container.dispatchEvent(new CustomEvent('mio-filter-submit', {
      bubbles: true,
      detail: { resetPage: resetPage }
    }));
  }

  // UI-only sync of a dropdown option into its cell header (no submit) —
  // used on load; real filtering on load happens server-side via the
  // before-paint default-metal redirect in mio-collection-product-grid.
  function syncOptionUI(option) {
    var cell = option.closest('[data-ft-cell]');
    if (!cell) return;
    var type = cell.dataset.ftCell;
    cell.querySelectorAll('[data-ft-option]').forEach(function (o) { o.classList.remove('is-active'); });
    option.classList.add('is-active');
    var valueEl = cell.querySelector('[data-ft-value]');
    if (valueEl) valueEl.textContent = option.dataset.ftLabel || option.textContent.trim();
    if (type === 'shape') {
      var headerIcon = cell.querySelector('[data-ft-header-icon]');
      var optionIcon = option.querySelector('.mio-ft-option-shape-icon');
      if (headerIcon) headerIcon.innerHTML = optionIcon ? optionIcon.outerHTML : '';
    }
    if (type === 'metal') {
      var headerDot = cell.querySelector('[data-ft-header-dot]');
      var optionDot = option.querySelector('.mio-ft-option-metal-dot');
      if (headerDot && optionDot) {
        headerDot.style.setProperty(
          '--mio-ft-metal-color',
          optionDot.style.getPropertyValue('--mio-ft-metal-color')
        );
      }
    }
  }

  // Reflect the URL's filter state in the headers. Defaults are NOT applied
  // here: every configured default is already in the URL via the before-paint
  // redirect in mio-collection-product-grid, so the toolbar only ever mirrors
  // what is actually filtering the grid.
  //
  // Previously shape and size painted their default as selected without
  // submitting it. The grid showed everything while the toolbar claimed a
  // shape, and because the default also left its checkbox ticked, the next
  // interaction serialised it and the filter appeared out of nowhere.
  // Re-check the toolbar's inputs from the URL, then mirror them into the
  // headers.
  //
  // The toolbar renders OUTSIDE #ProductGridContainer, so an AJAX facet render
  // never replaces it and its checkboxes keep whatever they were last set to.
  // Because onSubmit serialises the entire #FacetFiltersForm, a stale ticked
  // box silently re-applies its filter on the next interaction — remove the
  // "Round" chip in the drawer and the grid updates, but the toolbar still
  // holds Round checked and the next click brings it straight back. Same on
  // browser Back. The URL is the only source of truth, so re-derive from it.
  function syncFromUrl(container) {
    var params = new URLSearchParams(window.location.search);
    container.querySelectorAll('.mio-ft-input').forEach(function (inp) {
      if (!inp.name) return;
      inp.checked = params.getAll(inp.name).indexOf(inp.value) !== -1;
    });
    syncHeaders(container);
  }

  function syncHeaders(container) {
    ['shape', 'size', 'metal'].forEach(function (type) {
      var cell = container.querySelector('[data-ft-cell="' + type + '"]');
      if (!cell) return;

      var active = null;
      cell.querySelectorAll('.mio-ft-input').forEach(function (inp) {
        if (!active && inp.checked) active = inp.closest('[data-ft-option]');
      });

      if (active) {
        syncOptionUI(active);
        return;
      }

      // Nothing filtering this facet — rest on "All" where the cell offers it.
      var clearOpt = cell.querySelector('[data-ft-clear]');
      if (clearOpt) syncOptionUI(clearOpt);
    });
  }
})();
